// Theme system for FireGOLD ISProdukce React application
// Centralized design tokens extracted from CSS files

export const theme = {
  // Color palette
  colors: {
    // Primary gold colors
    gold: '#A89B6F',
    goldAlt: '#C9A14A',
    goldDark: 'rgb(119, 102, 5)',
    goldYellow: '#C49B00',

    // Base colors
    black: '#000000',
    white: '#ffffff',

    // Gray scale
    gray100: '#cccccc',
    gray200: '#e0e0e0',
    gray300: '#ccc',
    gray400: '#999',
    gray500: '#444',

    // Background colors
    bgDark: '#111',
    bgDarker: '#282c34',

    // Transparent overlays
    overlayDark: 'rgba(0, 0, 0, 0.8)',
    overlayDarker: 'rgba(0, 0, 0, 0.85)',
    overlayLight: 'rgba(0, 0, 0, 0.9)',

    // Transparent backgrounds
    whiteAlpha5: 'rgba(255, 255, 255, 0.05)',
    whiteAlpha15: 'rgba(255, 255, 255, 0.15)',
    whiteAlpha20: 'rgba(255, 255, 255, 0.2)',

    // Gold radial gradients
    goldRadialHover: 'radial-gradient(circle at center, rgba(255, 215, 0, 0.15) 0%, rgba(255, 215, 0, 0) 100%)',
    goldRadialLink: 'radial-gradient(circle at center, rgba(255, 215, 0, 0.15) 0%, rgba(255, 215, 0, 0) 50%)',
    goldRadialAlpha15: 'rgba(255, 215, 0, 0.15)',
    goldRadialAlpha30: 'rgba(255, 215, 0, 0.3)',

    // Card colors
    cardBg: 'rgba(57, 49, 0, 0.852)',
    cardBgHover: '#1c160a',
  },

  // Spacing
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    xxl: '32px',
    xxxl: '36px',

    // Specific component spacing
    controlBarHeight: 60,
    sidebarWidth: '500px',
    sidebarWidthMobile: '95vw',
  },

  // Font sizes
  fontSizes: {
    xs: '0.75rem',      // 12px
    sm: '0.9rem',       // 14.4px
    base: '1rem',       // 16px
    md: '1.1rem',       // 17.6px
    lg: '1.25rem',      // 20px
    xl: '1.5rem',       // 24px
    xxl: '1.8rem',      // 28.8px
    xxxl: '2rem',       // 32px
    huge: '2.5rem',     // 40px
    giant: '3rem',      // 48px
    mega: '4rem',       // 64px
    ultra: '4.5rem',    // 72px
  },

  // Font weights
  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // Border radius
  borderRadius: {
    sm: '4px',
    md: '6px',
    lg: '8px',
    xl: '100px',
  },

  // Transitions
  transitions: {
    fast: '0.1s ease',
    normal: '0.2s ease',
    slow: '0.3s ease',
    panel: '300ms ease',
  },

  // Animations
  animations: {
    fadeInDuration: '0.4s',
    fadeOutDuration: '0.1s',
    fadeInTiming: 'ease-out',
    fadeOutTiming: 'ease-in',
  },

  // Z-index layers
  zIndex: {
    base: 1,
    tooltip: 20,
    overlay: 20,
    panel: 30,
    modal: 100,
    transition: 100,
  },

  // Breakpoints (for media queries)
  breakpoints: {
    mobile: '480px',
    mobileLg: '540px',
    tablet: '600px',
    tabletLg: '700px',
    desktop: '950px',
    desktopMd: '1000px',
    desktopLg: '1200px',
  },

  // Sizes
  sizes: {
    logoHeight: '80px',
    logoHeightMobile: '40px',
    logoHeightMd: '60px',
    buttonIcon: '48px',
    maxContentWidth: '1200px',
  },

  // Shadows
  shadows: {
    panel: '-2px 0 8px rgba(0,0,0,0.3)',
    popup: '0 0 20px rgba(0,0,0,0.5)',
  },

  // Line heights
  lineHeights: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.6,
    loose: 2,
  },
} as const;

// Type for theme
export type Theme = typeof theme;

// Media query helpers
export const media = {
  mobile: `@media (max-width: ${theme.breakpoints.mobile})`,
  mobileLg: `@media (max-width: ${theme.breakpoints.mobileLg})`,
  tablet: `@media (max-width: ${theme.breakpoints.tablet})`,
  tabletLg: `@media (max-width: ${theme.breakpoints.tabletLg})`,
  desktop: `@media (max-width: ${theme.breakpoints.desktop})`,
  desktopMd: `@media (max-width: ${theme.breakpoints.desktopMd})`,
  desktopLg: `@media (max-width: ${theme.breakpoints.desktopLg})`,

  // Special media queries
  desktopLgOrShort: `@media (max-width: ${theme.breakpoints.desktopLg}) or (max-height: 800px)`,
  desktopLgOrVeryShort: `@media (max-width: ${theme.breakpoints.desktopLg}) or (max-height: 600px)`,
} as const;
